'use client'

import React, { useState, useCallback, useId, KeyboardEvent, ChangeEvent } from 'react'
import Icon from '../../atoms/Icon/Icon.component'
import * as S from './SearchInput.styles'

export type SearchInputProps = {
  placeholder?: string
  value?: string
  label: string
  submitButtonLabel: string
  onSearch: (value: string) => void
  onChange?: (value: string) => void
}

function SearchInput({
  placeholder = '',
  value: controlledValue,
  label,
  submitButtonLabel,
  onSearch,
  onChange,
}: SearchInputProps) {
  const [internalValue, setInternalValue] = useState('')
  const inputId = useId()
  const labelId = useId()

  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : internalValue

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      if (!isControlled) {
        setInternalValue(newValue)
      }
      onChange?.(newValue)
    },
    [isControlled, onChange]
  )

  const handleSearch = useCallback(() => {
    if (value.trim()) {
      onSearch(value.trim())
    }
  }, [value, onSearch])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSearch()
      }
    },
    [handleSearch]
  )

  return (
    <S.SearchInputWrapper role="search" aria-labelledby={labelId}>
      <S.SearchInputLabel id={labelId} htmlFor={inputId}>
        {label}
      </S.SearchInputLabel>
      <S.SearchInputIcon aria-hidden>
        <Icon name="magnifying-glass" aria-hidden />
      </S.SearchInputIcon>
      <S.SearchInputField
        id={inputId}
        type="search"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      <S.SearchInputButton
        type="button"
        onClick={handleSearch}
        aria-label={submitButtonLabel}
        disabled={!value.trim()}
      >
        <Icon name="arrow-right" size="small" aria-hidden />
      </S.SearchInputButton>
    </S.SearchInputWrapper>
  )
}

export default SearchInput
